/**
 * The one place that decides which URL a still is drawn from at which size.
 *
 * Every picture of the app comes from a permission-checked route of ours. A
 * route asked for a size (`?w=<n>`) answers with the smallest copy that fits:
 *
 *   - on Cloudflare (`apps/api/src/lib/cdn.ts`): a redirect to a signed
 *     Images variant, 240, 480, 960 or 1600 wide, AVIF or WebP as the
 *     browser accepts;
 *   - otherwise the small preview the generator cut (`?w=240`,
 *     `apps/api/src/lib/previews.ts`), or the stored derivative or master.
 *
 * A URL without `?w=` is the original, always: lightboxes, downloads and decks
 * keep full quality. A place that draws a still small asks for the preview and
 * lets `srcset` move up to bigger copies when the slot or the screen needs it.
 * When a Cloudflare copy fails to load, `fallBackToOrigin` retries the same
 * picture from our own origin (`&cdn=0`), so a blocked CDN costs speed, never
 * a picture.
 */
import type { SyntheticEvent } from "react";
import type { CdnStill } from "@scandi/shared";

/** The smallest width asked for (the server's `PREVIEW_WIDTH`, and Cloudflare's smallest variant). */
export const PREVIEW_WIDTH = 240;
/** The width of a stored `thumb` derivative. */
export const THUMB_WIDTH = 540;
/** The widths a route can answer from Cloudflare (`CDN_WIDTHS` on the server). */
const CDN_WIDTHS = [240, 480, 960, 1600] as const;

const withParam = (url: string, param: string) => `${url}${url.includes("?") ? "&" : "?"}${param}`;

/** `url` asking for a copy at least `width` wide (rounded up to a size the server keeps). */
export function sizedUrl(url: string, width: number): string {
  const w = CDN_WIDTHS.find((n) => n >= width) ?? CDN_WIDTHS[CDN_WIDTHS.length - 1];
  return withParam(url, `w=${w}`);
}

/** `url` asking for the small preview. */
export function previewUrl(url: string): string {
  return sizedUrl(url, PREVIEW_WIDTH);
}

/**
 * `src`, `srcSet` for a still: the preview first, then each larger copy known,
 * with its width. `larger` is ordered small to large; an entry without a width
 * is left out of the set (a browser must be told the truth about each width).
 * Each larger copy is asked for at a size too, so Cloudflare can answer it
 * with a variant; without Cloudflare the route answers the copy itself.
 */
export function previewSources(
  smallest: string,
  larger: Array<{ url: string | null | undefined; width: number | null | undefined }> = [],
): { src: string; srcSet: string } {
  const entries = [`${previewUrl(smallest)} ${PREVIEW_WIDTH}w`];
  const seen = new Set<number>([PREVIEW_WIDTH]);
  for (const { url, width } of larger) {
    if (!url || !width || width <= PREVIEW_WIDTH) continue;
    // A copy much wider than 960 also offers its 960 variant, for a mid-sized slot on a dense screen.
    const widths = width > 1200 ? [960, Math.min(width, 1600)] : [Math.min(width, 1600)];
    for (const w of widths) {
      if (seen.has(w)) continue;
      seen.add(w);
      entries.push(`${sizedUrl(url, w)} ${w}w`);
    }
  }
  return { src: previewUrl(smallest), srcSet: entries.join(", ") };
}

/**
 * `onError` for a sized still: the first failure retries the same picture
 * from our own origin (`&cdn=0`, without `srcset`), in case the CDN was
 * blocked. Answers true when it did; a caller with its own fallback chain
 * carries on when it answers false (the origin failed too, or the URL was not
 * a sized one).
 */
export function fallBackToOrigin(event: SyntheticEvent<HTMLImageElement>): boolean {
  const img = event.currentTarget;
  const asked = img.getAttribute("src") ?? "";
  if (img.dataset.cdnFallback || !/[?&]w=\d+/.test(asked) || asked.includes("cdn=0")) return false;
  img.dataset.cdnFallback = "1";
  img.removeAttribute("srcset");
  img.src = withParam(asked, "cdn=0");
  return true;
}

/**
 * The signed Cloudflare copies a list or document answer carries for a still (`CdnStill`), when they can still
 * be used: present, and not about to expire. A page drawn from a persisted or long-kept answer may hold copies
 * whose signatures have run out; those are ignored and the picture comes from our own route, as before.
 */
export function usableCdn(cdn: CdnStill | null | undefined): CdnStill | null {
  if (!cdn?.src) return null;
  return cdn.expiresAt * 1000 > Date.now() + 60_000 ? cdn : null;
}
